/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Router } from '@angular/router';
import { PopoverController, SelectCustomEvent } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription, takeUntil } from 'rxjs';

import { TypeUserEnum, User, UserRol } from '@core/models';
import { AuthService } from '@core/services/auth/auth.service';
import { IncofisaAuthService } from '@core/services/auth/incofisa-auth.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { PortfolioService } from '@core/services/portfolio/portfolio.service';
import { SplitPaneMenuService } from '@core/services/split-pane-menu/split-pane-menu.service';
import { WindowMeasuresService } from '@core/services/window-measures/window-measures.service';

import { environment } from '@environments/environment';

import { HeaderUserPopoverComponent } from '../header-user-popover/header-user-popover.component';

@Component({
  selector: 'el-buen-inquilino-header',
  templateUrl: './header.component.html',
  standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input()
  onlyLogo = false;

  @Output()
  logout = new EventEmitter();

  @Input()
  login = true;

  @Input() source: string;

  @Input() params: string;

  @Input() showLogout = true;

  @Input() title: string;

  @Input() isLanding = false;

  //Provisionalmente quitamos el cambio de lenguaje en produccion.
  isDisabledMultilanguage: boolean = environment.production;

  width: number;

  user: User;
  userRol: string;

  widthChangeSub: Subscription;
  showMenuBtnSub: Subscription;
  destroy$ = new Subject<void>();

  get showPortfolioSelect(): boolean {
    return this.user?.portfoliosManager && !!this.user?.portfolioId;
  }

  get portfolios(): string[] {
    return this.portfolioService.portfolios();
  }

  get showChangeLanguage(): boolean {
    return !this.isIncofisaUser();
  }

  get showPortfolioBtn(): boolean {
    return (this.isUser() || this.isIncofisaUser()) && this.width > 991;
  }

  constructor(
    private authenticateService: AuthService,
    private navigationService: NavigationService,
    private incofisaAuthService: IncofisaAuthService,
    private popoverController: PopoverController,
    private router: Router,
    private splitPaneMenuService: SplitPaneMenuService,
    private windowMeasuresService: WindowMeasuresService,
    private portfolioService: PortfolioService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    if (this.isUser()) {
      this.setUserFromAuth();
    } else if (this.isIncofisaUser()) {
      this.user = this.incofisaAuthService.user;
    }

    this.widthChangeSub = this.windowMeasuresService
      .getInnerWidth()
      .subscribe((width: number) => {
        this.width = width;
      });

    this.getPortfolios();
  }

  setUserFromAuth(): void {
    this.authenticateService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp: User) => {
        this.user = resp;
        if (this.user?.portfolioId) {
          this.userRol = this.translate.instant('common.user-roles.admin');
        }
        if (this.user?.apiRol === UserRol.API_ADMIN) {
          this.userRol = this.translate.instant('common.user-roles.manager');
        }
        if (this.user?.apiRol === UserRol.API_MEMBER) {
          this.userRol = this.translate.instant(
            'common.user-roles.collaborator'
          );
        }
      });
  }

  clickLogout(): void {
    let pageTo: string;
    if (localStorage.getItem('loginPage')) {
      pageTo = localStorage.getItem('loginPage');
      localStorage.removeItem('loginPage');
    }

    this.authenticateService.clearUser();
    this.incofisaAuthService.clearUser();
    this.navigationService.logout(null, pageTo || this.source);
    this.logout.emit();
  }

  getScreenInfo(): string {
    return (
      this.authenticateService?.user?.firstname +
      ' ' +
      this.authenticateService?.user?.surname1 +
      ' ' +
      this.authenticateService?.user?.surname2
    );
  }

  isUser(): boolean {
    return this.authenticateService?.isUserLogged();
  }

  isIncofisaUser(): boolean {
    return this.incofisaAuthService?.isIncofisaUser();
  }

  goHome(): void {
    if (this.authenticateService.isUserLogged()) {
      this.navigationService.goHome(this.authenticateService.user);
    } else {
      this.navigationService.goLoginPage();
    }
  }

  goLogin(): void {
    this.navigationService.goLoginPage();
  }

  clickLogo(): void {
    if (
      this.isUser() &&
      this.authenticateService.user.userType === TypeUserEnum.HOMEOWNER
    ) {
      if (this.checkUserSessionPayment()) {
        this.goHome();
      } else {
        this.navigationService.goToSubscriptionPage(
          this.authenticateService.user.id
        );
      }
    } else if (this.isIncofisaUser()) {
      this.navigationService.goToLandingBackoffice();
    } else {
      this.navigationService.goLanding();
    }
  }

  async showUserPopover(ev: any): Promise<void> {
    const isUser = this.isUser();
    const popover = await this.popoverController.create({
      component: HeaderUserPopoverComponent,
      event: ev,
      componentProps: {
        isUser
      }
    });
    await popover.present();

    const { data } = await popover.onWillDismiss();

    if (data) {
      switch (data.option) {
        case 'PROFILE': {
          const currentUrl = this.router.url.split('/');
          currentUrl.shift();
          currentUrl.pop();

          this.router.navigate([...currentUrl, 'profile']);
          break;
        }
        case 'HELP': {
          break;
        }
        case 'LOGOUT': {
          this.clickLogout();
          break;
        }
      }
    }
  }

  hideSplitPane(): void {
    this.splitPaneMenuService.setShowSplitPane();
  }

  onPortfolioChange(event: SelectCustomEvent<string>): void {
    this.portfolioService.updatePortfolio(this.user, event.detail.value);
  }

  ngOnDestroy(): void {
    this.widthChangeSub?.unsubscribe();
    this.showMenuBtnSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkUserSessionPayment(): boolean {
    const expirationDateSubscription = new Date(
      this.authenticateService.user.session.expirationDate
    );
    return (
      this.authenticateService.user.session &&
      this.authenticateService.user.session.paymentStatus &&
      expirationDateSubscription.getTime() > new Date().getTime()
    );
  }

  private async getPortfolios(): Promise<void> {
    // Se hace la llamada si es un portfolio manager y no se ha buscado todavia
    // portfolios del usuario
    if (
      this.user &&
      this.user.portfoliosManager &&
      this.portfolios.length === 0
    ) {
      this.portfolioService.getPortfolios(this.user);
    }
  }
}
